import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiBell, FiCalendar, FiCheck, FiClock, FiPlus, FiRefreshCw, FiRepeat, FiX } from 'react-icons/fi';
import { useToast } from '../../components/toast/toastProvider';
import ShiftsService, { Shift, ShiftChangeRequest, ShiftNotification } from '../../services/shiftsService';
import UserGroupsService, { User, UserGroup } from '../../services/userGroupsService';
import './ShiftsPage.css';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const startOfWeek = (date = new Date()) => {
  const next = new Date(date);
  const day = next.getDay();
  next.setDate(next.getDate() - day);
  next.setHours(0, 0, 0, 0);
  return next;
};

const dateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const toDateTimeLocal = (value: string | Date) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const displayUser = (user?: User | { username?: string; email?: string; name?: string }) => {
  const value = user as any;
  return value?.name || value?.username || value?.email || 'Unassigned';
};

const emptyShiftForm = {
  departmentId: '',
  employeeId: '',
  startAt: '',
  endAt: '',
  roleLabel: '',
  location: '',
  notes: '',
};

const emptyRequestForm = {
  type: 'edit' as 'edit' | 'swap',
  proposedStartAt: '',
  proposedEndAt: '',
  targetEmployeeId: '',
  reason: '',
};

const ShiftsPage: React.FC = () => {
  const { addToast } = useToast();
  const [weekStart, setWeekStart] = useState(dateInputValue(startOfWeek()));
  const [departments, setDepartments] = useState<UserGroup[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [requests, setRequests] = useState<ShiftChangeRequest[]>([]);
  const [notifications, setNotifications] = useState<ShiftNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [requestShift, setRequestShift] = useState<Shift | null>(null);
  const [shiftForm, setShiftForm] = useState(emptyShiftForm);
  const [requestForm, setRequestForm] = useState(emptyRequestForm);

  const selectedDepartment = departments.find(department => department.id === selectedDepartmentId);
  const employees = selectedDepartment?.members || [];
  const unreadCount = notifications.filter(notification => !notification.readAt).length;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [departmentResponse, shiftList, requestList, notificationList] = await Promise.all([
        UserGroupsService.getDepartments(),
        ShiftsService.getShifts({
          weekStart: new Date(weekStart).toISOString(),
          departmentId: selectedDepartmentId || undefined,
          employeeId: selectedEmployeeId || undefined,
        }),
        ShiftsService.getRequests('pending'),
        ShiftsService.getNotifications(),
      ]);
      setDepartments(departmentResponse.departments);
      setShifts(shiftList);
      setRequests(requestList);
      setNotifications(notificationList);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to Load Shifts',
        message: error instanceof Error ? error.message : 'Please try again.',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, selectedDepartmentId, selectedEmployeeId, weekStart]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const shiftsByDay = useMemo(() => {
    const week = new Date(`${weekStart}T00:00:00`);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(week);
      date.setDate(week.getDate() + index);
      const key = date.toDateString();
      return {
        date,
        shifts: shifts.filter(shift => new Date(shift.startAt).toDateString() === key),
      };
    });
  }, [shifts, weekStart]);

  const openCreate = () => {
    setEditingShift(null);
    setShiftForm({
      ...emptyShiftForm,
      departmentId: selectedDepartmentId,
      employeeId: selectedEmployeeId,
    });
    setShowCreate(true);
  };

  const openEdit = (shift: Shift) => {
    setEditingShift(shift);
    setShiftForm({
      departmentId: shift.departmentId,
      employeeId: shift.employeeId,
      startAt: toDateTimeLocal(shift.startAt),
      endAt: toDateTimeLocal(shift.endAt),
      roleLabel: shift.roleLabel || '',
      location: shift.location || '',
      notes: shift.notes || '',
    });
    setShowCreate(true);
  };

  const submitShift = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const payload = {
        ...shiftForm,
        startAt: new Date(shiftForm.startAt).toISOString(),
        endAt: new Date(shiftForm.endAt).toISOString(),
      };
      if (editingShift) {
        await ShiftsService.updateShift(editingShift.id, payload);
      } else {
        await ShiftsService.createShift(payload);
      }
      setShowCreate(false);
      setEditingShift(null);
      await loadData();
      addToast({ type: 'success', title: 'Shift Saved', message: 'Roster updated.', duration: 3000 });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Save Shift', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const cancelShift = async (shift: Shift) => {
    try {
      await ShiftsService.cancelShift(shift.id);
      await loadData();
      addToast({ type: 'success', title: 'Shift Cancelled', message: 'The shift was cancelled.', duration: 3000 });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Cancel Shift', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const openRequest = (shift: Shift, type: 'edit' | 'swap') => {
    setRequestShift(shift);
    setRequestForm({
      ...emptyRequestForm,
      type,
      proposedStartAt: toDateTimeLocal(shift.startAt),
      proposedEndAt: toDateTimeLocal(shift.endAt),
    });
  };

  const submitRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!requestShift) return;

    try {
      await ShiftsService.createRequest(requestShift.id, {
        type: requestForm.type,
        proposedStartAt: requestForm.type === 'edit' ? new Date(requestForm.proposedStartAt).toISOString() : undefined,
        proposedEndAt: requestForm.type === 'edit' ? new Date(requestForm.proposedEndAt).toISOString() : undefined,
        targetEmployeeId: requestForm.type === 'swap' ? requestForm.targetEmployeeId : undefined,
        reason: requestForm.reason,
      });
      setRequestShift(null);
      await loadData();
      addToast({ type: 'success', title: 'Request Sent', message: 'Your manager has been notified.', duration: 3000 });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Send Request', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const reviewRequest = async (request: ShiftChangeRequest, action: 'approve' | 'reject') => {
    try {
      if (action === 'approve') await ShiftsService.approveRequest(request.id);
      else await ShiftsService.rejectRequest(request.id);
      await loadData();
      addToast({
        type: 'success',
        title: action === 'approve' ? 'Request Approved' : 'Request Rejected',
        message: 'The approval queue has been updated.',
        duration: 3000,
      });
    } catch (error) {
      addToast({ type: 'error', title: 'Review Failed', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const markRead = async (notification: ShiftNotification) => {
    try {
      await ShiftsService.markNotificationRead(notification.id);
      setNotifications(prev => prev.map(item => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item));
    } catch {
      addToast({ type: 'error', title: 'Notification Error', message: 'Could not mark notification read.' });
    }
  };

  return (
    <div className="shifts-page">
      <header className="shifts-header">
        <div>
          <h1>Shifts</h1>
          <p>Manage weekly rosters, shift changes, and manager approvals.</p>
        </div>
        <div className="shifts-header-actions">
          <span className="shift-notification-pill"><FiBell /> {unreadCount} unread</span>
          <button className="shift-secondary-btn" onClick={loadData} disabled={loading}><FiRefreshCw /> Refresh</button>
          <button className="shift-primary-btn" onClick={openCreate}><FiPlus /> New Shift</button>
        </div>
      </header>

      <section className="shifts-filters">
        <label>
          <span>Week</span>
          <input type="date" value={weekStart} onChange={event => setWeekStart(event.target.value)} />
        </label>
        <label>
          <span>Department</span>
          <select value={selectedDepartmentId} onChange={event => { setSelectedDepartmentId(event.target.value); setSelectedEmployeeId(''); }}>
            <option value="">All managed departments</option>
            {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
          </select>
        </label>
        <label>
          <span>Employee</span>
          <select value={selectedEmployeeId} onChange={event => setSelectedEmployeeId(event.target.value)} disabled={!selectedDepartmentId}>
            <option value="">All employees</option>
            {employees.map(employee => <option key={employee.id} value={employee.id}>{displayUser(employee)}</option>)}
          </select>
        </label>
      </section>

      <main className="shifts-layout">
        <section className="weekly-roster">
          {shiftsByDay.map(day => (
            <div key={day.date.toISOString()} className="shift-day-column">
              <div className="shift-day-header">
                <strong>{dayNames[day.date.getDay()]}</strong>
                <span>{day.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
              </div>
              {day.shifts.length === 0 ? (
                <div className="shift-empty">No shifts</div>
              ) : day.shifts.map(shift => (
                <article key={shift.id} className={`shift-card status-${shift.status}`}>
                  <div className="shift-card-time"><FiClock /> {new Date(shift.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(shift.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  <h3>{shift.roleLabel || 'Shift'}</h3>
                  <p>{displayUser(shift.employee)}</p>
                  <small>{shift.department?.name || 'Department'}{shift.location ? ` • ${shift.location}` : ''}</small>
                  {shift.notes && <p className="shift-notes">{shift.notes}</p>}
                  <div className="shift-card-actions">
                    <button onClick={() => openRequest(shift, 'edit')}><FiCalendar /> Request edit</button>
                    <button onClick={() => openRequest(shift, 'swap')}><FiRepeat /> Swap</button>
                    <button onClick={() => openEdit(shift)}>Edit</button>
                    <button onClick={() => cancelShift(shift)}><FiX /> Cancel</button>
                  </div>
                </article>
              ))}
            </div>
          ))}
        </section>

        <aside className="shifts-side-panel">
          <section>
            <h2>Approval Queue</h2>
            {requests.length === 0 ? <p className="muted">No pending requests.</p> : requests.map(request => (
              <div key={request.id} className="request-card">
                <strong>{request.type === 'edit' ? 'Edit request' : 'Swap request'}</strong>
                <p>{displayUser(request.requester)} • {request.shift?.roleLabel || 'Shift'}</p>
                {request.type === 'swap' ? (
                  <small>Swap with {displayUser(request.targetEmployee)}</small>
                ) : (
                  <small>{request.proposedStartAt && new Date(request.proposedStartAt).toLocaleString()} - {request.proposedEndAt && new Date(request.proposedEndAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                )}
                {request.reason && <p className="muted">{request.reason}</p>}
                <div className="request-actions">
                  <button onClick={() => reviewRequest(request, 'approve')}><FiCheck /> Approve</button>
                  <button onClick={() => reviewRequest(request, 'reject')}><FiX /> Reject</button>
                </div>
              </div>
            ))}
          </section>

          <section>
            <h2>Notifications</h2>
            {notifications.length === 0 ? <p className="muted">No notifications.</p> : notifications.slice(0, 6).map(notification => (
              <button key={notification.id} className={`notification-card ${notification.readAt ? 'read' : ''}`} onClick={() => markRead(notification)}>
                <strong>{notification.title}</strong>
                <span>{notification.message}</span>
              </button>
            ))}
          </section>
        </aside>
      </main>

      {showCreate && (
        <div className="shift-modal-backdrop">
          <form className="shift-modal" onSubmit={submitShift}>
            <h2>{editingShift ? 'Edit Shift' : 'Create Shift'}</h2>
            <label>Department
              <select required value={shiftForm.departmentId} onChange={event => setShiftForm(prev => ({ ...prev, departmentId: event.target.value, employeeId: '' }))}>
                <option value="">Select department</option>
                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select>
            </label>
            <label>Employee
              <select required value={shiftForm.employeeId} onChange={event => setShiftForm(prev => ({ ...prev, employeeId: event.target.value }))}>
                <option value="">Select employee</option>
                {(departments.find(department => department.id === shiftForm.departmentId)?.members || []).map(employee => (
                  <option key={employee.id} value={employee.id}>{displayUser(employee)}</option>
                ))}
              </select>
            </label>
            <label>Start <input required type="datetime-local" value={shiftForm.startAt} onChange={event => setShiftForm(prev => ({ ...prev, startAt: event.target.value }))} /></label>
            <label>End <input required type="datetime-local" value={shiftForm.endAt} onChange={event => setShiftForm(prev => ({ ...prev, endAt: event.target.value }))} /></label>
            <label>Role <input value={shiftForm.roleLabel} onChange={event => setShiftForm(prev => ({ ...prev, roleLabel: event.target.value }))} placeholder="Site supervisor, electrician..." /></label>
            <label>Location <input value={shiftForm.location} onChange={event => setShiftForm(prev => ({ ...prev, location: event.target.value }))} /></label>
            <label>Notes <textarea value={shiftForm.notes} onChange={event => setShiftForm(prev => ({ ...prev, notes: event.target.value }))} /></label>
            <div className="shift-modal-actions">
              <button type="button" onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className="shift-primary-btn">Save Shift</button>
            </div>
          </form>
        </div>
      )}

      {requestShift && (
        <div className="shift-modal-backdrop">
          <form className="shift-modal" onSubmit={submitRequest}>
            <h2>{requestForm.type === 'edit' ? 'Request Shift Edit' : 'Request Shift Swap'}</h2>
            {requestForm.type === 'edit' ? (
              <>
                <label>Proposed start <input required type="datetime-local" value={requestForm.proposedStartAt} onChange={event => setRequestForm(prev => ({ ...prev, proposedStartAt: event.target.value }))} /></label>
                <label>Proposed end <input required type="datetime-local" value={requestForm.proposedEndAt} onChange={event => setRequestForm(prev => ({ ...prev, proposedEndAt: event.target.value }))} /></label>
              </>
            ) : (
              <label>Swap with
                <select required value={requestForm.targetEmployeeId} onChange={event => setRequestForm(prev => ({ ...prev, targetEmployeeId: event.target.value }))}>
                  <option value="">Select employee</option>
                  {(departments.find(department => department.id === requestShift.departmentId)?.members || employees).filter(employee => employee.id !== requestShift.employeeId).map(employee => (
                    <option key={employee.id} value={employee.id}>{displayUser(employee)}</option>
                  ))}
                </select>
              </label>
            )}
            <label>Reason <textarea value={requestForm.reason} onChange={event => setRequestForm(prev => ({ ...prev, reason: event.target.value }))} /></label>
            <div className="shift-modal-actions">
              <button type="button" onClick={() => setRequestShift(null)}>Cancel</button>
              <button type="submit" className="shift-primary-btn">Send Request</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ShiftsPage;
